// «Viendo ahora» in a channel / group / thread header (shared/API.md §39): up to three overlapping
// faces, the newest `since` on top, «+N» beyond them, and «Viendo ahora: Ana, Luis» as the tooltip
// (and the accessible name). Nothing is drawn when nobody else is looking.
import { useMemo } from 'react';
import { data } from '../../../bridge/client';
import { ViewingPresence } from '../../../core/viewingPresence';
import { t, tf } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { useLive } from '../../hooks';
import { useViewers } from './viewing';
import './viewing.css';

const NO_PEOPLE: Record<string, { name: string; version: number }> = {};

export function ViewingStack({ channelId, rootId, me, size = 22, className = '' }: { channelId: string; rootId: string; me: string; size?: number; className?: string }) {
  const viewers = useViewers(channelId, rootId);
  const shown = useMemo(() => ViewingPresence.others(viewers, me), [viewers, me]);
  const ids = shown.map((v) => v.userId);
  const { value: people } = useLive(() => (ids.length ? data.call('viewingPeople', ids) : Promise.resolve(NO_PEOPLE)), ['users'], [ids.join(',')]);
  if (!shown.length) return null;
  const { faces, overflow } = ViewingPresence.stack(shown);
  const nameOf = (id: string) => people?.[id]?.name ?? t('mac.unknownAuthor');
  const label = ViewingPresence.tooltip(shown.map((v) => nameOf(v.userId)));
  return (
    <span className={`viewing-stack${className ? ' ' + className : ''}`} title={label} aria-label={label} role="img" data-viewers={ids.join(',')}>
      {/* Newest first in the data; drawn so the newest sits ON TOP of the overlap. */}
      {faces.map((v, i) => (
        <span key={v.userId} className="viewing-face" style={{ zIndex: faces.length - i }}>
          <Avatar userId={v.userId} name={nameOf(v.userId)} version={people?.[v.userId]?.version ?? 0} size={size} />
        </span>
      ))}
      {overflow > 0 ? <span className="viewing-more">{tf('web.viewing.more', overflow)}</span> : null}
    </span>
  );
}
